import { useState, useEffect, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { createUseStyles } from "react-jss";
import Emitter from "services/UtilsService/Emitter";
import { EuiToolTip, EuiFlexGroup, EuiFlexItem, EuiButtonEmpty, EuiTextArea } from "@elastic/eui";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { Flyout } from "components/Flyout/Flyout";
import SkeletonPlaceholder from "components/SkeletonPlaceholder";
import { isNullOrUndefined, toUseCase } from "utils";
import WisdomAdvisorService from "services/WisdomAdvisor/WisdomAdvisorService";
import { message_text_styles } from "./WisdomAdvisorStyles";
import Button from "components/Buttons/Button";
import IconButton from "components/Buttons/IconButton";
import Popover from "components/Popover/Popover";
import { FDTitle } from "components/Title/FDTitle";
import AutoGrowEuiTextArea from "./AutoGrowTextArea";
import { Card } from "components/Cards/Card";
import { SparkleIcon } from "components/Icons";
import { parseMarkdown, sanitizeHtml } from "./WisdomAdvisorUtils";
const useStyles = createUseStyles(message_text_styles);

const WisdomAdvisor = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const typingIntervalRef = useRef<number | null>(null);

  const [isOpen, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [messages, setMessages] = useState<any[]>([]); // completed messages
  const [currentResponse, setCurrentResponse] = useState<string[] | null>(null); // incoming response chunks
  const [question, setQuestion] = useState<string>("");
  const [currentQuestion, setCurrentQuestion] = useState<string>(""); // question currently being typed/shown
  const [displayed, setDisplayed] = useState<string>(""); // typed text for currentResponse
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [showBottomButtons, setShowBottomButtons] = useState<boolean>(false);
  const [showConversations, setShowConversations] = useState<boolean>(false);
  const [popoverIsOpen, setPopoverOpen] = useState<any>(null);
  const [conversations, setConversations] = useState<any[]>([]);
  const [prompts, setPrompts] = useState<any[]>([]);
  const [renameId, setRenameId] = useState<any>(null);
  const [reName, setReName] = useState<any>(null);
  const [hoverId, setHoverId] = useState<any>(null);

  const { clientId, WisdomAdvisorContext, isAskingAdvisor } = useSelector(
    globalFilterSelector,
    shallowEqual
  );

  const getConversations = () => {
    WisdomAdvisorService.getConversations({ clientId }).then((response) => {
      setConversations(response?.conversations);
    });
  };

  const getSuggestedPrompts = () => {
    const screenPath = window.location.pathname;

    WisdomAdvisorService.getSuggestedPrompts({
      clientId,
      useCase: "wisdom_advisor_chat",
      tag: screenPath,
    }).then((response) => {
      setPrompts(response?.prompts);
    });
  };

  const getConversation = (id: string | number) => {
    if (!id) return;
    setLoading(true);
    WisdomAdvisorService.getSingleConversation({ clientId, conversationId: id }).then(
      (response) => {
        if (isNullOrUndefined(response)) return;
        setConversationId(response.id);
        setMessages(response.messages);
        setLoading(false);
      }
    );
  };

  useEffect(() => {
    if (!isOpen) return;
    getConversations();
    getSuggestedPrompts();
  }, [clientId, isOpen]);

  useEffect(() => {
    if (isNullOrUndefined(WisdomAdvisorContext)) return;

    setLoading(true);
    setShowBottomButtons(false);
    setOpen(true);

    const {
      context,
      columns,
      data,
      conversationId: incomingConversationId,
      datasets,
    } = WisdomAdvisorContext;
    const payload = {
      conversationId: incomingConversationId,
      context,
      useCase:
        question?.length > 0 || conversationId
          ? "wisdom_advisor_chat"
          : "wisdom_advisor_ui_control", //toUseCase(context), //"wisdom_advisor_ui_control",
      datasets:
        datasets?.length > 0
          ? datasets
          : [
              {
                columnNames: columns,
                data,
              },
            ],
      ...(question?.length === 0 &&
        isNullOrUndefined(conversationId) && {
          metadata: [
            {
              key: "ui_element",
              value: toUseCase(context),
            },
          ],
        }),
    };

    WisdomAdvisorService.question({ clientId, payload }).then((response) => {
      if (response?.isAborted) {
        setLoading(false);
        return;
      }
      if (response?.errors) {
        setMessages((prev) => [
          ...prev,
          {
            answer: "Something went wrong! Please try again later.",
            question: question || null,
            questionId: null,
          },
        ]);
        setLoading(false);
        return;
      }

      const _messageChunks = response?.map((item: any, idx: number) => {
        if (idx === 0) {
          setConversationId(item.conversationId);
          setQuestionId(item.questionId);
        }
        return item.textChunk;
      });

      setCurrentQuestion(question || "");
      setCurrentResponse(_messageChunks);
      setQuestion("");
      setLoading(false);
      getConversations();
    });
  }, [WisdomAdvisorContext]);

  useEffect(() => {
    if (typingIntervalRef.current) {
      window.clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }

    if (!currentResponse || currentResponse.length === 0) {
      return;
    }

    setDisplayed("");
    setShowBottomButtons(false);

    const chunks = currentResponse;
    let i = 0;
    typingIntervalRef.current = window.setInterval(() => {
      setDisplayed((prev) => prev + (chunks[i] ?? ""));
      i++;
      if (i >= chunks.length) {
        if (typingIntervalRef.current) {
          window.clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        setShowBottomButtons(true);

        setMessages((prev) => [
          ...prev,
          {
            questionId,
            question: currentQuestion?.length > 0 ? currentQuestion : null,
            answer: chunks.join(""),
          },
        ]);

        setCurrentResponse(null);
        setCurrentQuestion("");
      }
    }, 40);

    return () => {
      if (typingIntervalRef.current) {
        window.clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    };
  }, [currentResponse]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) {
      setTimeout(() => {
        try {
          el.scrollTo({
            top: el.scrollHeight,
            behavior: "smooth",
          });
        } catch (err) {
          el.scrollTop = el.scrollHeight;
        }
      }, 50);
    }
  }, [messages, displayed]);

  const onReset = () => {
    dispatch({
      payload: null,
      type: globalFilterTypes.SET_WISDOM_ADVISOR_CONTEXT,
    });
    setMessages([]);
    setCurrentResponse(null);
    setQuestion("");
    setDisplayed("");
    setCurrentQuestion("");
    setConversationId(null);
    setQuestionId(null);
    setRate(null);
    setShowBottomButtons(false);
    setLoading(false);
    Emitter.emit("cancelRequest", {});
  };

  const onClose = () => {
    onReset();
    setShowConversations(false);
    setOpen(false);

    if (isAskingAdvisor) {
      //FLYOUT Ask Advisor Action in proggress
      dispatch({
        type: globalFilterTypes.SET_ASK_ADVISOR_FLAG,
        payload: false,
      });
    }
  };

  const onChange = (e: any) => {
    setQuestion(e.target.value);
  };

  const onAsk = () => {
    setQuestionId(null);
    setRate(null);
    if (question?.length > 0) {
      dispatch({
        payload: {
          conversationId,
          context: sanitizeHtml(question, true),
          columns: [],
          data: [],
        },
        type: globalFilterTypes.SET_WISDOM_ADVISOR_CONTEXT,
      });
    }
  };

  // const handleRate = (rating: any) => {
  //   if (!questionId) return;
  //   WisdomAdvisorService.rateAnswer({ clientId, payload: { questionId, rating } }).then(() => {
  //     setQuestionId(null);
  //   });
  // };

  const clean_html = parseMarkdown(sanitizeHtml(displayed));
  const visibleMessages = messages || [];

  const onConversationUpdate = (id: any) => {
    WisdomAdvisorService.updateSingleConversation({
      clientId,
      conversationId: id,
      payload: { conversationName: reName },
    }).then(() => {
      setReName(null);
      setRenameId(null);
      getConversations();
    });
  };

  const onConversationDelete = (id: any) => {
    WisdomAdvisorService.deleteSingleConversation({ clientId, conversationId: id }).then(() => {
      getConversations();
    });
  };

  const sparkleIcon = (
    <span style={{ paddingRight: "4px", paddingTop: "4px" }}>
      <SparkleIcon />
    </span>
  );

  return (
    <div>
      <span style={{ paddingRight: "4px" }}>
        <Button
          type="empty"
          text={
            <div style={{ display: "flex", alignItems: "center" }}>
              {sparkleIcon}
              <b style={{ color: "var(--advisor-color)" }}>WIS</b>
              <span style={{ color: "var(--brand-color)", fontWeight: 800 }}>dom</span>
              <span
                style={{
                  color: "var(--advisor-color)",
                  paddingLeft: "4px",
                  fontSize: "var(--fs13)",
                  fontWeight: 600,
                }}>
                Advisor
              </span>
            </div>
          }
          onClick={() => setOpen(true)}
          style={{ color: "#1b8dcd" }}
        />
      </span>
      {isOpen && (
        <Flyout
          size={"700px"}
          title={
            <div style={{ position: "relative" }}>
              <div style={{ display: "inline-flex", alignItems: "center" }}>
                {sparkleIcon}
                <b style={{ color: "#fff", fontSize: "var(--fs16)" }}>WIS</b>
                <span
                  style={{ color: "var(--brand-color)", fontSize: "var(--fs16)", fontWeight: 600 }}>
                  dom
                </span>
                <span
                  style={{
                    color: "#fff",
                    paddingLeft: "4px",
                    fontSize: "var(--fs16)",
                    fontWeight: 500,
                  }}>
                  Advisor
                </span>
              </div>
            </div>
          }
          headerChildren={
            <span
              style={{
                marginTop: "4px",
                fontSize: "var(--fs12)",
                fontWeight: "400",
                marginLeft: "14px",
                padding: "2px 8px",
                color: "#a0a6b0",
              }}>
              AI-powered performance & cost analysis
            </span>
          }
          sideChildren={
            <div>
              {showConversations && (
                <div
                  style={{
                    position: "absolute",
                    top: "0px",
                    height: "100vh",
                    width: "300px",
                    right: "700px",
                    background: "var(--app-background)",
                    borderRight: "1px solid var(--border-color)",
                  }}>
                  <div style={{ position: "fixed" }}>
                    <FDTitle
                      className="properties-panel-title"
                      showBottomSpace={false}
                      title={
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <IconButton
                            type={"menuRight"}
                            onClick={() => {
                              setShowConversations(false);
                            }}
                            color={"var(--text)"}
                            style={{
                              width: "34px",
                              height: "34px",
                              marginLeft: "8px",
                              marginTop: "-16px",
                            }}
                          />

                          <div
                            style={{ padding: "16px", color: "var(--text)", marginBottom: "12px" }}>
                            Conversations
                          </div>
                        </div>
                      }
                      titleSize="xs"
                    />
                  </div>
                  <div
                    style={{
                      marginTop: "50px",
                      height: "calc(100vh - 50px)",
                      overflowY: "scroll",
                    }}>
                    {conversations?.map((i, idx) => (
                      <div
                        className={renameId !== i.id ? classes.conversation : classes.rename}
                        style={
                          conversationId === i.id
                            ? {
                                backgroundColor: "var(--conversation)",
                                borderRadius: "6px",
                                border: "1px solid var(--border-color)",
                              }
                            : {}
                        }
                        onClick={() => {
                          getConversation(i.id);
                        }}
                        onMouseOver={() => {
                          setHoverId(i.id);
                        }}
                        onMouseOut={() => {
                          setHoverId(null);
                        }}
                        onMouseLeave={() => {
                          if (renameId) onConversationUpdate(i.id);
                        }}>
                        <EuiFlexGroup
                          gutterSize="none"
                          alignItems="center"
                          justifyContent="spaceBetween">
                          {renameId !== i.id && (
                            <EuiToolTip
                              position="top"
                              content={i.name}
                              css={{ backgroundColor: "#506382" }}>
                              <EuiFlexItem>
                                {i.name?.length > 30 ? `${i.name?.slice(0, 30)}...` : i.name}
                              </EuiFlexItem>
                            </EuiToolTip>
                          )}

                          {renameId == i.id && (
                            <EuiFlexItem>
                              <EuiTextArea
                                rows={2}
                                value={reName}
                                onChange={(e) => {
                                  setReName(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault(); // stop newline insertion
                                    onConversationUpdate(i.id);
                                  }
                                }}
                                style={{
                                  borderRadius: "4px",
                                  padding: "4px",
                                  resize: "none",
                                }}
                                fullWidth
                              />
                            </EuiFlexItem>
                          )}

                          <EuiFlexItem
                            grow={false}
                            style={{
                              visibility:
                                hoverId === i.id && renameId !== i.id ? "visible" : "hidden",
                            }}>
                            <Popover
                              button={
                                <IconButton
                                  size={"xs"}
                                  iconSize="s"
                                  color="var(--text)"
                                  type={"boxesVertical"}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setPopoverOpen(idx);
                                  }}
                                />
                              }
                              className="fd-table-cell-filter-popover"
                              onClose={() => {
                                setPopoverOpen(null);
                              }}
                              isOpen={popoverIsOpen === idx}
                              ownFocus
                              panelPaddingSize="none"
                              children={
                                <EuiFlexGroup
                                  direction="column"
                                  gutterSize="none"
                                  alignItems="flexStart"
                                  style={{ padding: 5, paddingTop: 10 }}>
                                  <EuiFlexItem>
                                    <EuiButtonEmpty
                                      color="text"
                                      style={{ color: "var(--text)" }}
                                      iconType={"pencil"}
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        setRenameId(i.id);
                                        setReName(i.name);
                                        setPopoverOpen(null);
                                      }}
                                      size="s"
                                      type="button">
                                      {"Rename"}
                                    </EuiButtonEmpty>
                                  </EuiFlexItem>
                                  <EuiFlexItem grow={false}>
                                    <EuiButtonEmpty
                                      color="text"
                                      style={{ color: "var(--text)" }}
                                      iconType={"trash"}
                                      onClick={(e: any) => {
                                        e.stopPropagation();
                                        onConversationDelete(i.id);
                                        setPopoverOpen(null);
                                      }}
                                      size="s"
                                      type="button">
                                      {"Delete"}
                                    </EuiButtonEmpty>
                                  </EuiFlexItem>
                                </EuiFlexGroup>
                              }
                            />
                          </EuiFlexItem>
                        </EuiFlexGroup>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          }
          children={
            <>
              <div
                ref={containerRef}
                style={{
                  height: "calc(100vh - 160px)",
                  overflowY: "auto",
                  paddingRight: "12px",
                  marginTop: "-6px",
                  marginLeft: "-6px",
                  marginRight: "-14px",
                }}>
                {/* Completed messages */}
                {visibleMessages.length > 0 &&
                  visibleMessages.map((i, idx) => (
                    <div key={`msg-${idx}`} style={{ marginBottom: 12 }}>
                      {i?.question && (
                        <div
                          className={classes.formattedText}
                          style={{
                            backgroundColor: "var(--questions)",
                            textAlign: "right",
                            padding: "12px 16px",
                            display: "inline-block",
                            borderRadius: "25px 25px 25px 0",
                            border: "0.5px solid #ffd35f",
                            marginBottom: 24,
                          }}>
                          {i?.question}
                        </div>
                      )}

                      <div style={{ paddingBottom: "8px" }}>
                        <div
                          className={classes.formattedText}
                          dangerouslySetInnerHTML={{
                            __html: parseMarkdown(sanitizeHtml(i?.answer)),
                          }}
                        />
                      </div>
                    </div>
                  ))}

                {!loading && displayed?.length === 0 && visibleMessages.length === 0 && (
                  <>
                    <div
                      style={{
                        fontWeight: "500",
                        fontSize: "var(--fs16)",
                        padding: "16px 8px",
                        color: "var(--markTitle)",
                      }}>
                      <span> Got a question for your Advisor?</span>
                    </div>
                    {prompts?.length > 0 && (
                      <div
                        style={{
                          fontWeight: "500",
                          fontSize: "var(--fs14)",
                          marginTop: "32px",
                          padding: "8px",
                          color: "var(---text)",
                          borderRadius: "6px",
                        }}>
                        <div
                          style={{
                            fontWeight: "500",
                            fontSize: "var(--fs16)",
                            padding: "8px 0px",
                            marginBottom: "16px",
                          }}>
                          ✨ Suggestions
                        </div>

                        <EuiFlexGroup direction="column" gutterSize="m">
                          {prompts?.map((p) => (
                            <EuiFlexItem>
                              <Card
                                backgroundColor={"var(--app-background)"}
                                borderColor={"var(--brand-color)"}
                                borderPosition={"left"}
                                children={<>{p?.prompt}</>}
                                style={{
                                  padding: "10px 20px",
                                  borderLeft: "1px solid var(--primary-color)",
                                  fontWeight: "400",
                                }}
                                onClick={() => {
                                  setQuestion(p?.prompt);

                                  dispatch({
                                    payload: {
                                      conversationId: null,
                                      context: sanitizeHtml(p?.prompt, true),
                                      datasets: [
                                        {
                                          columnNames: [],
                                          data: [],
                                        },
                                      ],
                                    },
                                    type: globalFilterTypes.SET_WISDOM_ADVISOR_CONTEXT,
                                  });
                                }}
                                clickable
                              />
                            </EuiFlexItem>
                          ))}
                        </EuiFlexGroup>
                      </div>
                    )}
                  </>
                )}

                {/* Current question bubble (being asked right now) */}
                {!loading && currentQuestion?.length > 0 && (
                  <div
                    className={classes.formattedText}
                    style={{
                      backgroundColor: "var(--questions)",
                      textAlign: "right",
                      padding: "12px 16px",
                      display: "inline-block",
                      borderRadius: "25px 25px 25px 0",
                      border: "0.5px solid #ffd35f",
                      marginBottom: 24,
                    }}>
                    {currentQuestion}
                  </div>
                )}

                {/* Typing / displayed current response (animated) */}
                {displayed?.length > 0 && !showBottomButtons && !loading && (
                  <div style={{ position: "relative", marginTop: 8 }}>
                    <div
                      className={classes.formattedText}
                      dangerouslySetInnerHTML={{ __html: clean_html }}
                    />
                  </div>
                )}

                {/* Loading skeleton appears below existing conversation so previous responses remain visible */}
                {loading && (
                  <div style={{ marginTop: visibleMessages.length > 0 ? 24 : 8 }}>
                    <div
                      style={{
                        marginBottom: "8px",
                        fontWeight: "500",
                        fontSize: "var(--fs16)",
                        padding: "4px 8px",
                        paddingBottom: "16px",
                        color: "var(--markTitle)",
                      }}>
                      <span style={{ display: "inline-flex", alignItems: "center" }}>
                        {sparkleIcon}{" "}
                      </span>
                      <b style={{ color: "var(--advisor-color)" }}>Analyzing...</b>
                      <div
                        style={{
                          marginTop: "8px",
                          fontWeight: "500",
                          fontSize: "var(--fs14)",
                        }}>
                        {WisdomAdvisorContext?.context || question}
                      </div>
                    </div>
                    <SkeletonPlaceholder isLoading={loading} />
                  </div>
                )}

                {/* Bottom action buttons for the current response (copy / thumbs) */}
                {/* {!loading && displayed?.length > 0 && showBottomButtons && (
                  <div
                    style={{
                      display: "flex",
                      gap: 0,
                      marginBottom: 16,
                      marginTop: "-24px",
                    }}>
                    <EuiCopy textToCopy={cleanText(displayed)}>
                      {(copy) => (
                        <EuiButtonIcon
                          iconType="copy"
                          size="m"
                          onClick={copy}
                          style={{ color: "var(--cyan)" }}
                        />
                      )}
                    </EuiCopy>
                    {rate !== 1 && (
                      <EuiToolTip position="top" content="Good response" repositionOnScroll={true}>
                        <IconButton
                          size={"s"}
                          icon={getThumbUpIcon(rate)}
                          onClick={() => {
                            setRate(5);
                            handleRate(5);
                          }}
                        />
                      </EuiToolTip>
                    )}
                    {rate !== 5 && (
                      <EuiToolTip position="top" content="Bad response" repositionOnScroll={true}>
                        <IconButton
                          size={"s"}
                          icon={getThumbDownIcon(rate)}
                          onClick={() => {
                            setRate(1);
                            handleRate(1);
                          }}
                        />
                      </EuiToolTip>
                    )}
                  </div>
                )} */}
              </div>
            </>
          }
          onClose={onClose}
          footerChildren={
            <EuiFlexGroup>
              <EuiFlexItem
                style={{ position: "relative", margin: "-4px -8px" }}
                onKeyDown={(key: any) => {
                  if (key?.code === "Enter") onAsk();
                }}>
                <AutoGrowEuiTextArea
                  value={question}
                  onChange={onChange}
                  maxRows={6}
                  //@ts-ignore
                  placeholder="Ask Anything"
                  isPlusAllowed={messages?.length > 0}
                />

                {messages?.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 5,
                      left: 6,
                    }}>
                    <IconButton
                      type={"plus"}
                      onClick={() => {
                        onReset();
                      }}
                      color={"var(--text)"}
                      loading={false}
                      style={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "24px",
                      }}
                    />
                  </span>
                )}

                {conversations?.length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 5,
                      right: 42,
                    }}>
                    <IconButton
                      type={showConversations ? "menuRight" : "menuLeft"}
                      onClick={() => {
                        setShowConversations(!showConversations);
                      }}
                      color={"var(--text)"}
                      loading={false}
                      style={{
                        backgroundColor: showConversations
                          ? "var(--app-background)"
                          : "transparent",
                        width: "34px",
                        height: "34px",
                        borderRadius: "24px",
                      }}
                    />
                  </span>
                )}

                <span
                  style={{
                    position: "absolute",
                    bottom: 5,
                    right: 6,
                  }}>
                  <IconButton
                    type={question?.length > 0 ? "sortUp" : "sparkles"}
                    onClick={onAsk}
                    color={question?.length > 0 ? "#fff" : "var(--text)"}
                    loading={loading}
                    style={{
                      backgroundColor:
                        question?.length > 0 ? "var(--button-bg)" : "var(--app-background)",
                      width: "34px",
                      height: "34px",
                      borderRadius: "24px",
                    }}
                  />
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>
          }
        />
      )}
    </div>
  );
};

export default WisdomAdvisor;
